import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PropertyLocale } from '@common/entities';
import {
  setAvailableLanguages,
  setLocale,
  setPropertyCountryCode,
  setPropertyFormatting,
  setPropertyId,
} from 'state/slices/localeSlice';
import { RootState } from 'state/store';

import { getViewerPropertyLanguages } from 'services/translations';
import {
  resetFormattingLocale,
  setFormattingLocale,
  setFormattingSettings,
} from 'utils/formatting/formattingLocale';
import { DEFAULT_US_UNITS_OF_MEASURE } from 'utils/formatting/units';
import { getBrowserLocale } from 'utils/locale';

interface UsePropertyLocaleProps {
  orgId: number | null | undefined;
  propertyId: number | null | undefined;
  forcePropertyDefault?: boolean;
}

function setActiveLocale(locale: string): void {
  const i18n = (
    globalThis as typeof globalThis & {
      i18nUtils?: { setLocale?: (activeLocale: string) => void };
    }
  ).i18nUtils;
  if (typeof i18n?.setLocale === 'function') i18n.setLocale(locale);
}

function resetToBrowserLocale(): string {
  const fallbackLocale = getBrowserLocale();
  resetFormattingLocale();
  setActiveLocale(fallbackLocale);
  return fallbackLocale;
}

export function getEffectivePropertyLocale(
  languages: Array<{ localeCode: string; isDefault: boolean }>,
  currentLocale: string,
  forcePropertyDefault = false,
): string | undefined {
  if (
    !forcePropertyDefault &&
    currentLocale &&
    currentLocale !== getPropertyDefaultLocale(languages) &&
    languages.some((language) => language.localeCode === currentLocale)
  ) {
    return undefined;
  }

  return languages.find((language) => language.isDefault)?.localeCode ?? languages[0]?.localeCode;
}

function getPropertyDefaultLocale(
  languages: Array<{ localeCode: string; isDefault: boolean }>,
): string | undefined {
  return languages.find((language) => language.isDefault)?.localeCode ?? languages[0]?.localeCode;
}

function getConfiguredPropertyDefaultLocale(
  languages: Array<{ localeCode: string; isDefault: boolean }>,
): string | undefined {
  return languages.find((language) => language.isDefault)?.localeCode;
}

function normalizeLocale(localeCode: string | null | undefined): string | undefined {
  const locale = localeCode?.trim().replace(/_/g, '-');
  return locale || undefined;
}

function normalizeLanguages(languages: PropertyLocale[]): PropertyLocale[] {
  return languages.map((language) => ({
    ...language,
    localeCode: normalizeLocale(language.localeCode) ?? language.localeCode,
  }));
}

function getPropertyFormattingLocale(
  locationLocaleCode: string | null | undefined,
  languages: Array<{ localeCode: string; isDefault: boolean }>,
): string | undefined {
  return (
    getConfiguredPropertyDefaultLocale(languages) ??
    normalizeLocale(locationLocaleCode) ??
    getPropertyDefaultLocale(languages)
  );
}

function getActiveFormattingLocale(
  languages: Array<{ localeCode: string; isDefault: boolean }>,
  currentLocale: string,
  propertyLocale: string | null | undefined,
  forcePropertyDefault: boolean,
): string | undefined {
  if (
    !forcePropertyDefault &&
    currentLocale &&
    currentLocale !== getPropertyDefaultLocale(languages) &&
    languages.some((language) => language.localeCode === currentLocale)
  ) {
    return currentLocale;
  }

  if (forcePropertyDefault) return getPropertyDefaultLocale(languages);

  return getPropertyFormattingLocale(propertyLocale, languages);
}

/**
 * Loads the property's configured languages so locale formatting also works
 * on screens that do not render a language switcher (for example Lead Profile).
 */
export function usePropertyLocale({
  orgId,
  propertyId,
  forcePropertyDefault = false,
}: UsePropertyLocaleProps): { isReady: boolean } {
  const dispatch = useDispatch();
  const {
    localeCode,
    availableLanguages,
    propertyId: cachedPropertyId,
    propertyFormatting,
    propertyCountryCode,
  } = useSelector((state: RootState) => state.locale);
  const inFlightRequestKey = useRef<string | null>(null);
  const settledRequestKey = useRef<string | null>(null);
  const latestLocaleRef = useRef(localeCode);
  const [readyRequestKey, setReadyRequestKey] = useState<string | null>(null);
  const requestKey = orgId && propertyId ? `${orgId}:${propertyId}` : null;
  latestLocaleRef.current = localeCode;

  useEffect(() => {
    if (!requestKey || !orgId || !propertyId) return undefined;
    if (cachedPropertyId === propertyId && availableLanguages.length > 0 && propertyCountryCode) {
      const propertyDefaultLocale = getPropertyDefaultLocale(availableLanguages);
      setFormattingSettings(propertyFormatting ?? undefined);
      setFormattingLocale(
        getActiveFormattingLocale(
          availableLanguages,
          latestLocaleRef.current,
          propertyFormatting?.localeCode,
          forcePropertyDefault,
        ),
      );

      if (forcePropertyDefault) {
        if (propertyDefaultLocale && propertyDefaultLocale !== latestLocaleRef.current) {
          setActiveLocale(propertyDefaultLocale);
          dispatch(setLocale(propertyDefaultLocale));
        }
      }
      setReadyRequestKey(requestKey);
      return undefined;
    }

    if (settledRequestKey.current === requestKey) return undefined;
    if (inFlightRequestKey.current === requestKey) return undefined;

    settledRequestKey.current = null;
    inFlightRequestKey.current = requestKey;
    setReadyRequestKey(null);

    dispatch(setPropertyId(propertyId));
    dispatch(setAvailableLanguages([]));
    dispatch(setPropertyFormatting(null));
    dispatch(setPropertyCountryCode(''));
    resetFormattingLocale();

    const applyFallbackLocale = (): void => {
      const fallbackLocale = resetToBrowserLocale();
      dispatch(setLocale(fallbackLocale));
      dispatch(setPropertyFormatting(null));
    };

    getViewerPropertyLanguages(orgId, propertyId)
      .then((property) => {
        if (inFlightRequestKey.current !== requestKey) return;

        const languages = normalizeLanguages(property?.languages ?? []);

        // Some production release tracks omit these locale fields. Hydrate the
        // viewer with US defaults so every consumer of the shared formatting
        // state (cart, payments, floor plans, and pet options) stays usable.
        const nextPropertyFormatting = {
          currencyCode: property?.currencyCode?.trim() || 'USD',
          currencySymbol: property?.currencySymbol,
          currencyDigits: property?.currencyDigits,
          numberFormat: property?.numberFormat,
          measurementSystem: property?.measurementSystem,
          unitsOfMeasure: {
            area: property?.unitsOfMeasure?.area ?? DEFAULT_US_UNITS_OF_MEASURE.area,
            distance: property?.unitsOfMeasure?.distance ?? DEFAULT_US_UNITS_OF_MEASURE.distance,
            weight: property?.unitsOfMeasure?.weight ?? DEFAULT_US_UNITS_OF_MEASURE.weight,
          },
          unitOfMeasureForArea: property?.unitOfMeasureForArea,
          unitOfMeasureForWeight: property?.unitOfMeasureForWeight,
          unitOfMeasureForDistance: property?.unitOfMeasureForDistance,
          localeCode: getPropertyFormattingLocale(property?.location?.localeCode, languages),
        };

        dispatch(setPropertyFormatting(nextPropertyFormatting));
        dispatch(
          setPropertyCountryCode(
            property?.location?.countryCode?.trim().toUpperCase() ||
              property?.countryCode?.trim().toUpperCase() ||
              '',
          ),
        );
        setFormattingSettings(nextPropertyFormatting);

        if (languages.length > 0) {
          dispatch(setAvailableLanguages(languages));
          const formattingLocale = getActiveFormattingLocale(
            languages,
            latestLocaleRef.current,
            property?.location?.localeCode,
            forcePropertyDefault,
          );
          setFormattingLocale(formattingLocale);

          const effectiveLocale = getEffectivePropertyLocale(
            languages,
            latestLocaleRef.current,
            forcePropertyDefault,
          );
          if (effectiveLocale) {
            setActiveLocale(effectiveLocale);
            dispatch(setLocale(effectiveLocale));
          }
        } else {
          const fallbackLocale = getBrowserLocale();
          setFormattingLocale(fallbackLocale);
          setActiveLocale(fallbackLocale);
          dispatch(setLocale(fallbackLocale));
        }
        settledRequestKey.current = requestKey;
        setReadyRequestKey(requestKey);
      })
      .catch(() => {
        if (inFlightRequestKey.current === requestKey) {
          applyFallbackLocale();
          settledRequestKey.current = requestKey;
          setReadyRequestKey(requestKey);
        }
      })
      .finally(() => {
        if (inFlightRequestKey.current === requestKey) {
          inFlightRequestKey.current = null;
        }
      });

    return undefined;
  }, [
    availableLanguages,
    cachedPropertyId,
    dispatch,
    forcePropertyDefault,
    localeCode,
    propertyFormatting,
    propertyCountryCode,
    orgId,
    propertyId,
    requestKey,
  ]);

  return { isReady: requestKey === null || readyRequestKey === requestKey };
}
